/**
 * Responsibility: attribute frame time to the phase that spent it, and report frames that ran long.
 *
 * `frameTimer.js` answers "how fast is it going" in one number for the on-screen readout. This answers a
 * different and harder question: **when a frame is lost, whose fault was it.** Those are separate jobs and
 * the second one needs data the first deliberately throws away.
 *
 * ## The one distinction worth building this for
 *
 * A reported 58 fps with an occasional hitch has two completely different causes and they need opposite
 * fixes:
 *
 * - **Our JavaScript overran.** The work between two `requestAnimationFrame` callbacks took longer than a
 *   refresh interval, so the display had nothing new to show and repeated the previous image. Fixed by
 *   doing less work per frame, or spreading it.
 * - **The frame was lost outside our JavaScript.** The work was quick, and the gap between callbacks was
 *   still two refresh intervals — GPU back-pressure from the bloom pass, compositor scheduling, a garbage
 *   collection between frames, or the browser simply not hitting vsync. Doing less work would not help at
 *   all.
 *
 * Telling them apart needs *both* numbers per frame: the interval the browser actually delivered, and the
 * work we did inside it. Neither alone can distinguish the two, which is why the existing frame timer
 * cannot answer this and why guessing from an average fps is how a session gets spent optimising the wrong
 * thing.
 *
 * ## Why phases rather than a profiler flame chart
 *
 * A browser profiler gives far more detail and cannot be handed to someone flying the application on their
 * own machine, which is where the problem is being reported. Four numbers a frame, logged as a summary
 * every few seconds, is something a person can paste into a message.
 *
 * Allocates nothing per frame: phase durations live in a typed array and the summary is built only when it
 * is logged.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

/** Milliseconds per second. */
const MS_PER_SECOND = 1000;

/**
 * The phases a frame is divided into, in the order they run.
 *
 * A fixed list rather than arbitrary labels, so durations live in a typed array and an unrecognised name
 * fails loudly instead of being silently dropped into a bucket nobody reads.
 */
export const FRAME_PHASES = ['sim', 'tiles', 'view', 'render'];

/** Slowest interval that can plausibly be a display refresh, in ms. Anything quicker is a bogus timestamp. */
const MIN_PLAUSIBLE_REFRESH_MS = 4;

/**
 * Create a frame profiler.
 *
 * @param {object} options
 * @param {boolean} options.enabled - When false every method is a no-op, so the call sites can stay
 *   unconditional.
 * @param {number} options.longFrameMs - Work above this counts as a long frame. Should sit below one
 *   refresh interval, since the point is to catch frames heading for an overrun.
 * @param {number} options.lateFrameFactor - Interval above `refresh * this` counts as a lost display frame.
 * @param {number} options.summarySeconds - Seconds between summaries.
 * @param {number} options.logLimit - Individual long-frame lines per summary window. A cap, because at
 *   sixty frames a second an uncapped log is unreadable and slows the thing it is measuring.
 * @param {(message: string) => void} [options.log] - Where reports go. Injected so tests can read them.
 * @param {() => number} [options.now] - Monotonic clock in ms.
 * @returns {object}
 */
export function createFrameProfiler({
  enabled,
  longFrameMs,
  lateFrameFactor,
  summarySeconds,
  logLimit,
  log = (message) => console.info(message),
  now = () => performance.now(),
}) {
  if (!(longFrameMs > 0)) throw new Error('createFrameProfiler: longFrameMs must be positive');
  if (!(lateFrameFactor > 1)) {
    // At or below 1 every frame is "late", since no interval is shorter than the refresh.
    throw new Error('createFrameProfiler: lateFrameFactor must be greater than 1');
  }
  if (!(summarySeconds > 0)) throw new Error('createFrameProfiler: summarySeconds must be positive');
  if (!Number.isInteger(logLimit) || logLimit < 0) {
    throw new Error('createFrameProfiler: logLimit must be a non-negative integer');
  }

  const phaseCount = FRAME_PHASES.length;

  /** Name to index, built once. Keeps `mark` a lookup rather than a search. */
  const phaseIndex = new Map(FRAME_PHASES.map((name, index) => [name, index]));

  // --- the frame in progress ------------------------------------------------

  const current = new Float64Array(phaseCount);
  let markedAt = 0;
  let intervalMs = 0;
  let inFrame = false;

  // --- the window being summarised ------------------------------------------

  const phaseTotals = new Float64Array(phaseCount);
  const worstPhases = new Float64Array(phaseCount);

  let frames = 0;
  let intervalTotal = 0;
  let shortestInterval = Number.POSITIVE_INFINITY;
  let longFrames = 0;
  let lateFrames = 0;
  let lateWithLongWork = 0;
  let worstWork = 0;
  let worstWorkInterval = 0;
  let worstWorkBuilds = 0;
  let builds = 0;
  let buildFrames = 0;
  let buildMsTotal = 0;
  let worstBuildMs = 0;
  let lateAfterBuild = 0;
  let logged = 0;
  let sinceSummary = 0;

  /*
    The previous frame's work and build count, carried across.

    **Blame for a lost frame belongs to the frame before it.** A late callback is how the loss becomes
    visible from inside the page, but the work that caused it had already finished — so deciding whether
    our own JavaScript is responsible means comparing against what the *previous* frame did, not this one.
  */
  let previousWork = 0;
  let previousBuilds = 0;

  /** Draw calls from the most recent frame. A level, not a total, so it is not averaged. */
  let lastDrawCalls = 0;

  function resetWindow() {
    phaseTotals.fill(0);
    worstPhases.fill(0);

    frames = 0;
    intervalTotal = 0;
    shortestInterval = Number.POSITIVE_INFINITY;
    longFrames = 0;
    lateFrames = 0;
    lateWithLongWork = 0;
    worstWork = 0;
    worstWorkInterval = 0;
    worstWorkBuilds = 0;
    builds = 0;
    buildFrames = 0;
    buildMsTotal = 0;
    worstBuildMs = 0;
    lateAfterBuild = 0;
    logged = 0;
    sinceSummary = 0;
  }

  /**
   * Best estimate of the display's refresh interval, in ms.
   *
   * **The shortest interval seen, not the average.** The average is dragged upward by exactly the lost
   * frames being measured, so using it would define the problem out of existence — at 58 fps average, a
   * 33 ms interval is only 1.9× the average and the threshold would let it through. The shortest interval
   * in a window of a few hundred frames is a frame the browser did deliver on time, which is what a
   * refresh interval is.
   */
  function refreshMs() {
    return Number.isFinite(shortestInterval) && shortestInterval >= MIN_PLAUSIBLE_REFRESH_MS
      ? shortestInterval
      : 0;
  }

  /** One frame's phase breakdown, formatted. */
  function describe(phases) {
    let text = '';
    for (let i = 0; i < phaseCount; i += 1) {
      text += `${i === 0 ? '' : ' + '}${FRAME_PHASES[i]} ${phases[i].toFixed(1)}`;
    }
    return text;
  }

  function total(phases) {
    let sum = 0;
    for (let i = 0; i < phaseCount; i += 1) sum += phases[i];
    return sum;
  }

  function summarise() {
    const refresh = refreshMs();
    const seconds = sinceSummary;
    const perSecond = seconds > 0 ? lateFrames / seconds : 0;

    const lines = [
      `[profile] ${seconds.toFixed(1)}s  ${frames} frames  ` +
        `${(intervalTotal > 0 ? (frames * MS_PER_SECOND) / intervalTotal : 0).toFixed(1)} fps  ` +
        `refresh ${refresh.toFixed(1)} ms  ${lastDrawCalls} draw calls`,

      /*
        The load-bearing line. Late frames the display lost, split by whether our own work was long
        enough to explain them — if most are in the second bucket, no amount of doing less work will help.
      */
      `  lost frames ${lateFrames} (${perSecond.toFixed(1)}/s):  ` +
        `${lateWithLongWork} our work ran long,  ${lateFrames - lateWithLongWork} lost outside our JS`,

      `  long work frames ${longFrames}, of which ${lateAfterBuild} were followed by a lost frame`,

      `  worst work ${worstWork.toFixed(1)} ms  (interval ${worstWorkInterval.toFixed(1)} ms, ` +
        `${worstWorkBuilds} tile builds)  = ${describe(worstPhases)}`,

      `  average per frame  = ${describe(
        Float64Array.from(phaseTotals, (value) => (frames > 0 ? value / frames : 0)),
      )}`,
    ];

    if (buildFrames > 0) {
      lines.push(
        `  tile builds ${builds} in ${buildFrames} frames  ` +
          `avg ${(buildMsTotal / buildFrames).toFixed(1)} ms  worst ${worstBuildMs.toFixed(1)} ms`,
      );
    } else {
      lines.push('  tile builds 0 — nothing was streaming during this window');
    }

    log(lines.join('\n'));
  }

  return {
    /** Whether anything is being measured. Call sites use it to skip gathering extras like draw counts. */
    isEnabled() {
      return enabled;
    },

    /**
     * Start a frame.
     *
     * @param {number} elapsedSeconds - Time since the previous frame, from the animation-frame
     *   timestamps. This is what the *display* did, and it is half of the whole point of this module.
     */
    beginFrame(elapsedSeconds) {
      if (!enabled) return;

      current.fill(0);
      markedAt = now();

      intervalMs = Number.isFinite(elapsedSeconds) && elapsedSeconds > 0 ? elapsedSeconds * MS_PER_SECOND : 0;
      inFrame = true;
    },

    /**
     * Close off a phase, attributing everything since the last mark to it.
     *
     * @param {string} name - One of `FRAME_PHASES`.
     */
    mark(name) {
      if (!enabled || !inFrame) return;

      const index = phaseIndex.get(name);
      if (index === undefined) {
        // Loudly, because a typo would otherwise quietly lose a phase and the totals would still look sane.
        throw new Error(`frameProfiler: unknown phase "${name}"`);
      }

      const at = now();
      current[index] += at - markedAt;
      markedAt = at;
    },

    /**
     * End a frame and fold it into the window.
     *
     * @param {number} [tilesBuilt] - Tiles built during this frame, from the tile manager's own report.
     *   Attribution needs it: a 25 ms `tiles` phase means something different from a 0.4 ms one.
     * @param {number} [drawCalls] - Draw calls the renderer submitted. Only reported in the summary, and
     *   only useful for the case where frames are lost while our own work is quick.
     */
    endFrame(tilesBuilt = 0, drawCalls = 0) {
      if (!enabled || !inFrame) return;
      inFrame = false;

      const work = total(current);
      const refresh = refreshMs();

      frames += 1;

      if (intervalMs > 0) {
        intervalTotal += intervalMs;
        if (intervalMs < shortestInterval && intervalMs >= MIN_PLAUSIBLE_REFRESH_MS) {
          shortestInterval = intervalMs;
        }
        sinceSummary += intervalMs / MS_PER_SECOND;
      }

      for (let i = 0; i < phaseCount; i += 1) phaseTotals[i] += current[i];

      lastDrawCalls = drawCalls;

      const long = work > longFrameMs;
      if (long) longFrames += 1;

      /*
        A frame is "late" on the evidence of the interval that *led into* it: the browser handing us a
        callback two refreshes after the last one is how a lost frame is visible from inside the page.
        The work that caused it was the previous frame's, which is why the previous frame's length is what
        decides the blame.
      */
      const late = refresh > 0 && intervalMs > refresh * lateFrameFactor;

      if (late) {
        lateFrames += 1;
        if (previousWork > longFrameMs) lateWithLongWork += 1;
        if (previousBuilds > 0) lateAfterBuild += 1;
      }

      if (work > worstWork) {
        worstWork = work;
        worstWorkInterval = intervalMs;
        worstWorkBuilds = tilesBuilt;
        worstPhases.set(current);
      }

      if (tilesBuilt > 0) {
        builds += tilesBuilt;
        buildFrames += 1;
        buildMsTotal += current[phaseIndex.get('tiles')];
        if (current[phaseIndex.get('tiles')] > worstBuildMs) {
          worstBuildMs = current[phaseIndex.get('tiles')];
        }
      }

      /*
        Individual lines for the first few long frames of each window. The summary says how often and how
        badly; these say what one actually looked like, which is what distinguishes a build spike from a
        render spike.
      */
      if (long && logged < logLimit) {
        logged += 1;
        log(
          `[profile] long frame: work ${work.toFixed(1)} ms, interval ${intervalMs.toFixed(1)} ms, ` +
            `${tilesBuilt} builds  = ${describe(current)}`,
        );
      }

      previousWork = work;
      previousBuilds = tilesBuilt;

      if (sinceSummary >= summarySeconds) {
        summarise();
        resetWindow();
      }
    },

    /**
     * The window's figures, for a caller that wants to display them rather than log them.
     *
     * **Added because the person reporting the problem reads a screenshot, not a console.** A summary in the
     * console needs devtools open, and devtools open changes the thing being measured — so the numbers that
     * decide the diagnosis have to be available to the on-screen readout as well.
     *
     * @returns {{frames: number, phases: Array<{name: string, averageMs: number}>, worstMs: number,
     *   worstPhases: Array<{name: string, ms: number}>, lostFrames: number, lostToOurWork: number,
     *   builds: number, worstBuildMs: number}|null} Null when nothing has been recorded, so the caller
     *   needs no special case.
     */
    snapshot() {
      if (!enabled || frames === 0) return null;

      let workTotal = 0;
      for (let i = 0; i < phaseCount; i += 1) workTotal += phaseTotals[i];

      return {
        frames,

        /*
          Our own work per frame, against the interval the display actually delivered.

          **The comparison that answers the question outright, without depending on any threshold.** The
          late-frame counters cannot help when *every* frame is uniformly slow: the shortest interval in the
          window is then also the slow one, so it becomes the refresh estimate and nothing is ever "late". But
          the gap between these two numbers has no such blind spot. Work of 5 ms inside a 25 ms frame means
          twenty milliseconds went somewhere our JavaScript was not running, and no amount of doing less can
          recover it. Work of 24 ms inside a 25 ms frame means the opposite.
        */
        averageWorkMs: workTotal / frames,
        averageIntervalMs: intervalTotal > 0 ? intervalTotal / frames : 0,

        phases: FRAME_PHASES.map((name, index) => ({
          name,
          averageMs: phaseTotals[index] / frames,
        })),
        worstMs: worstWork,
        worstPhases: FRAME_PHASES.map((name, index) => ({ name, ms: worstPhases[index] })),
        lostFrames: lateFrames,
        lostToOurWork: lateWithLongWork,
        builds,
        worstBuildMs,
      };
    },

    /**
     * Summarise the window now and start a fresh one.
     *
     * The periodic report is what a person flying the application gets. This is for asking on demand —
     * from the console, or from a test that needs the boundary to be somewhere it chose rather than
     * wherever the interval happened to land.
     */
    report() {
      if (!enabled || frames === 0) return;
      summarise();
      resetWindow();
    },

    /** Forget the window. Call after a pause or a world change, so the stall is not counted. */
    reset() {
      if (!enabled) return;
      resetWindow();
      previousWork = 0;
      previousBuilds = 0;
      inFrame = false;
    },
  };
}
